import {Component, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {ConfirmationService} from 'primeng/api';
import {Subscription} from 'rxjs';
import {ObjectBaseComponent} from '../../../../core/shared/object/mvs-object-base/object-base.component';
import {MvsCoreService} from '../../../../core/shared/service/mvs-core.service';
import {MvsMessageService} from '../../../../core/shared/service/message/mvs-message.service';
import {ObserverService} from '../../../../core/shared/object/service/observer.service';
import {NavigationItem} from '../../../../core/shared/dto/navigation/navigation-item';
import {WidgetData} from '../../../../core/shared/widget/widget-data';
import {WidgetFactory} from '../../../../core/shared/widget/service/widget.factory';
import {FilterCriteria} from '../../../../core/shared/filter/api/filter.criteria';
import {
    WidgetFunctionCallBackCreate,
    WidgetToolbarCreateInterface
} from '../../../../core/shared/helper/widget-function-call-back-create';
import {ObjectIdentifierData} from '../../../../core/shared/basic/object-identifier-data';
import {ObjectIdentifier} from '../../../../core/shared/basic/object-identifier';
import {MvsObjectNavigationService} from '../../../../core/shared/navigation/mvs-object-navigation.service';
import {MvsObjectNavigationEntry} from '../../../../core/shared/navigation/mvs-object-navigation-entry';
import {MvsObjectNavigationActionEnum} from '../../../../core/shared/navigation/mvs-object-navigation-action-enum';

/**
 * AI:
 * Status: "in progress"
 * Type: ObjectComponent
 * SubType: ObjectBaseComponent
 * Reason: ObjectComponent for hb.IntegrationBiproVersion — shows Auth-Typen and System-Lieferungen as child nav items
 */
@Component({
    selector: 'mvs-integration-bipro-version-object',
    templateUrl: './integration-bipro-version-object.component.html',
    styleUrls: ['./integration-bipro-version-object.component.scss'],
    standalone: false
})
export class IntegrationBiproVersionObjectComponent
    extends ObjectBaseComponent
    implements OnInit, OnChanges, OnDestroy {

    navigationItems: NavigationItem[] = [
        {
            label: 'Auth-Typen',
            action: 'authTypes',
            tooltip: 'Auth-Typen',
            icon: 'fa-regular fa-key',
            toggleable: false,
            default: true
        },
        {
            label: 'System-Lieferungen',
            action: 'systemShipments',
            tooltip: 'System-BiPRO-Lieferungen',
            icon: 'fa-regular fa-truck',
            toggleable: false,
            default: false
        }
    ];

    authTypeWidget: WidgetData;
    systemShipmentWidget: WidgetData;

    private queryParamSubscription: Subscription;

    constructor(
        protected override coreService: MvsCoreService,
        protected override messageService: MvsMessageService,
        protected override confirmationService: ConfirmationService,
        protected override observerService: ObserverService,
        protected navigationService: MvsObjectNavigationService,
        protected route: ActivatedRoute
    ) {
        super(coreService, messageService, confirmationService, observerService);
    }

    override ngOnInit(): void {
        super.ngOnInit();

        this.queryParamSubscription = this.route.queryParams.subscribe(params => {
            const navItemAction = params['navItem'];
            if (navItemAction) {
                const found = this.navigationItems.find(item => item.action === navItemAction);
                if (found) {
                    this.activeNavigationItems = found;
                }
            } else if (!this.activeNavigationItems) {
                const defaultItem = this.navigationItems.find(item => item.default) || this.navigationItems[0];
                this.activeNavigationItems = defaultItem;
            }
        });
    }

    override onObjectChanged(): void {
        this.handleNavigationItems(this.navigationItems);
        this.createChildWidgets();
    }

    private createChildWidgets(): void {
        const versionId = this.objectIdentifier.objectId;

        /**
         * AI:
         * Status: "in progress"
         * Type: Widget
         * SubType: Runtime
         * Reason: Auth-Typen table widget for IntegrationBiproVersion — filtered by biproVersion FK
         */
        const authTypeFilter = [FilterCriteria.create('e.biproVersionId', FilterCriteria.cOperatorEqual, versionId)];
        this.authTypeWidget = WidgetFactory.createWidgetTableEntityFkeyResolveQl(
            'hb.integration-bipro-version.object.auth-types',
            'Auth-Typen',
            'hb.IntegrationBiproAuthType',
            authTypeFilter
        );
        const authTypeCallbackFields: WidgetToolbarCreateInterface[] = [{fieldName: 'biproVersionDtoId', fieldValue: versionId}];
        this.authTypeWidget.functionCallbacks = WidgetFunctionCallBackCreate.widgetToolbarCreate(authTypeCallbackFields);

        /**
         * AI:
         * Status: "in progress"
         * Type: Widget
         * SubType: Runtime
         * Reason: System-Lieferungen table widget for IntegrationBiproVersion — filtered by biproVersion FK
         */
        const shipmentFilter = [FilterCriteria.create('e.biproVersionId', FilterCriteria.cOperatorEqual, versionId)];
        this.systemShipmentWidget = WidgetFactory.createWidgetTableEntityFkeyResolveQl(
            'hb.integration-bipro-version.object.system-shipments',
            'System-BiPRO-Lieferungen',
            'hb.IntegrationSystemBiproShipment',
            shipmentFilter
        );
        const shipmentCallbackFields: WidgetToolbarCreateInterface[] = [{fieldName: 'biproVersionDtoId', fieldValue: versionId}];
        this.systemShipmentWidget.functionCallbacks = WidgetFunctionCallBackCreate.widgetToolbarCreate(shipmentCallbackFields);
    }

    handleObjectSelect(event: ObjectIdentifierData | ObjectIdentifier): void {
        const entry = MvsObjectNavigationEntry.createNavigationEntry(
            event.objectType, event.objectId, 'object', '', '', '',
            MvsObjectNavigationActionEnum.edit
        );
        this.navigationService.navigateTo(entry, 'right');
    }

    override ngOnChanges(changes: SimpleChanges): void {
        super.ngOnChanges(changes);
    }

    override ngOnDestroy(): void {
        this.queryParamSubscription?.unsubscribe();
        super.ngOnDestroy();
    }
}
